import React from 'react';
import { translate } from 'translations/i18n';
import AssignUserPopover from 'components/shared/AssignUsers/AssignUserPopover';
import { Steven } from 'helpers/steven';
import toast from 'helpers/toast';
import { type ProjectDatabaseId } from 'data/v1';
import {
  useUpdateProjectTask,
  getSourceFromTaskAssets,
  useGetAssignableUsers,
  PROJECT_TASK_SOURCE,
} from 'data/v3';
import type {
  TaskObject,
  TaskImageObject,
  TaskVideoObject,
  AssignedUserObject,
} from 'data/v3/data-contracts';

type AssignUserToProjectTaskProps = {
  task: TaskObject;
  projectDbId: ProjectDatabaseId;
  assetDbId: TaskImageObject['id'] | TaskVideoObject['id'];
  assignedUsers: AssignedUserObject[];
  isGalleryView: boolean;
  trigger: React.ReactNode;
};

const AssignUserToProjectTask = ({
  task,
  projectDbId,
  assetDbId,
  assignedUsers,
  isGalleryView,
  trigger,
}: AssignUserToProjectTaskProps) => {
  const { assignableUsers, error } = useGetAssignableUsers({
    projectDbId,
  });

  const updateProjectTask = useUpdateProjectTask({
    projectDbId,
    assetDbId,
    onSuccess: ({ updatedTask, updatedAction }) => {
      if (updatedTask) {
        const source = getSourceFromTaskAssets({
          task: updatedTask,
        });

        Steven.entityCrud({
          entity_id: updatedTask.id.toString(),
          entity_type: 'project_task',
          action: 'updated',
          updated_action: updatedAction,
          // A task currently will never have both images and videos
          // This may change in the future and should be updated accordingly
          source: isGalleryView ? source : PROJECT_TASK_SOURCE.PROJECT,
        });
      }
    },
    onError: () => {
      toast.error(translate('projectTask.assignUserErroMsg'));
    },
  });

  const onAssignUser = (user: AssignedUserObject) => {
    updateProjectTask.mutate({
      task: {
        ...task,
        assignees: [
          {
            id: user.id,
            ...user,
          },
        ],
      },
    });
  };

  const onRemoveUser = (id: AssignedUserObject['id']) => {
    updateProjectTask.mutate({
      task: {
        ...task,
        assignees: [
          {
            id,
            _destroy: true,
          },
        ],
      },
    });
  };

  return (
    <AssignUserPopover
      assignedUsers={assignedUsers}
      assignableUsers={assignableUsers}
      trigger={trigger}
      onAssignUser={onAssignUser}
      onRemoveUser={onRemoveUser}
      hasGetAssignableUsersError={!!error}
    />
  );
};

export default AssignUserToProjectTask;
